import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { COMPANY, WHATSAPP_URL } from "@/lib/constants";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { trackConversion } from "@/lib/tracking";

const TEL_URL = `tel:${COMPANY.phoneRaw}`;

const links = [
  { href: "#services", label: "Services" },
  { href: "#about", label: "À propos" },
  { href: "#realisations", label: "Réalisations" },
  { href: "#avis", label: "Avis" },
  { href: "#devis", label: "Devis" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-11 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-white shadow-[0_2px_20px_-8px_rgba(0,0,0,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-20">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={scrolled ? "/logos/logo-icon-dark.png" : "/logos/logo-icon-white.png"}
            alt="DEM BAT — maison icône"
            className="h-10 w-auto transition-opacity duration-500 group-hover:opacity-80"
          />
          <div className="flex items-baseline gap-2">
            <span
              className={`font-serif text-2xl tracking-tight transition-colors ${
                scrolled ? "text-charcoal" : "text-white"
              }`}
            >
              DEM
            </span>
            <span
              className={`font-serif text-2xl italic font-light tracking-tight transition-colors ${
                scrolled ? "text-bronze" : "text-bronze-light"
              }`}
            >
              BAT
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className={`text-sm tracking-wide transition-colors hover:text-bronze ${
                scrolled ? "text-charcoal" : "text-white/90"
              }`}
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/candidatures"
            data-testid="nav-link-candidatures"
            className={`text-sm tracking-wide transition-colors hover:text-bronze ${
              scrolled ? "text-charcoal" : "text-white/90"
            }`}
          >
            Candidatures
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={TEL_URL}
            onClick={trackConversion(TEL_URL)}
            data-testid="nav-call-link"
            className={`flex items-center gap-2 text-sm transition-colors hover:text-bronze ${
              scrolled ? "text-charcoal" : "text-white"
            }`}
          >
            <Phone className="w-4 h-4" strokeWidth={1.5} />
            {COMPANY.phone}
          </a>
          <a
            href={WHATSAPP_URL}
            onClick={trackConversion(WHATSAPP_URL)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-link"
            aria-label="Discuter sur WhatsApp"
            className="flex items-center justify-center w-9 h-9 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white transition-all duration-300 hover:scale-105"
          >
            <WhatsAppIcon className="w-4 h-4" />
          </a>
          <a
            href="#devis"
            data-testid="nav-cta-quote"
            className="bg-bronze hover:bg-bronze-hover text-white px-6 py-3 text-xs uppercase tracking-widest transition-colors"
          >
            Devis gratuit
          </a>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <a
            href={WHATSAPP_URL}
            onClick={trackConversion(WHATSAPP_URL)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-mobile"
            aria-label="Discuter sur WhatsApp"
            className="flex items-center justify-center w-9 h-9 rounded-full bg-[#25D366] text-white"
          >
            <WhatsAppIcon className="w-4 h-4" />
          </a>
          <button
            onClick={() => setOpen(!open)}
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            data-testid="nav-mobile-toggle"
            className={scrolled ? "text-charcoal" : "text-white"}
          >
            {open ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-white border-t border-border shadow-[0_8px_24px_-12px_rgba(0,0,0,0.1)]"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
                className="text-charcoal text-base tracking-wide hover:text-bronze transition-colors"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/candidatures"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-link-candidatures"
              className="text-charcoal text-base tracking-wide hover:text-bronze transition-colors"
            >
              Candidatures
            </Link>
            <a
              href="#devis"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-cta"
              className="bg-bronze text-white px-6 py-3 text-xs uppercase tracking-widest text-center mt-2"
            >
              Devis gratuit
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
